import * as React from 'react';

import {SegmentedControl} from 'shells/card/segmented-control';
import {Checkbox} from 'shells/checkbox/checkbox';
import {Stack} from 'shells/layout/stack/stack';
import {SavedSettingToast} from 'shells/saved-setting-toast/saved-setting-toast';
import type {SavedSettingToastState} from 'shells/saved-setting-toast/saved-setting-toast';
import {Body} from 'shells/typography/body';

import type {
    ChatSettingsIntakeFormTypeEnum,
    ChatSettingsIntakeFormFieldsEnum,
} from 'nutshell-graphql-types';
import type {ChatSettingsIntakeFormInput} from 'nutshell-graphql-types';

import {
    SAVE_STATE_KEYS,
    CHATBOT_COLLECT_VISITOR_INFORMATION_OPTIONS,
    PHONE_FIELD,
    EMAIL_FIELD,
    COMPANY_FIELD,
} from './consts';
import type {SaveStateKey} from './consts';

type Props = {
    intakeFormType: ChatSettingsIntakeFormTypeEnum;
    setIntakeFormType: (value: ChatSettingsIntakeFormTypeEnum) => void;
    intakeFormFields: Array<ChatSettingsIntakeFormFieldsEnum>;
    setIntakeFormFields: (value: Array<ChatSettingsIntakeFormFieldsEnum>) => void;
    handleSave: (updates: ChatSettingsIntakeFormInput, saveStateKey: SaveStateKey) => Promise<any>;
    toastState: SavedSettingToastState;
    savingToastKey: SaveStateKey | null | undefined;
};

function toggleField(
    field: ChatSettingsIntakeFormFieldsEnum,
    selectedFields: Array<ChatSettingsIntakeFormFieldsEnum>
): Array<ChatSettingsIntakeFormFieldsEnum> {
    if (selectedFields.includes(field)) {
        return selectedFields.filter((f) => f !== field);
    }

    return [...selectedFields, field];
}

export const CollectVisitorInfoOptions = (props: Props) => {
    return (
        <Stack spacing={16}>
            <Stack spacing={8}>
                <SavedSettingToast
                    state={
                        props.savingToastKey === SAVE_STATE_KEYS.CHAT_INTAKE_FORM_TYPE
                            ? props.toastState
                            : 'initial'
                    }
                >
                    <Body isBold={true} size='small'>
                        How to collect information
                    </Body>
                </SavedSettingToast>
                <Body size='small'>
                    Collect the visitor’s contact information after the first chatbot reply
                </Body>
                <SegmentedControl
                    buttonOptions={CHATBOT_COLLECT_VISITOR_INFORMATION_OPTIONS}
                    selectedValue={props.intakeFormType}
                    setSelectedValue={(value) => {
                        props.setIntakeFormType(value);
                        props.handleSave({type: value}, SAVE_STATE_KEYS.CHAT_INTAKE_FORM_TYPE);
                    }}
                    isAnimated={true}
                />
            </Stack>
            <Stack spacing={8}>
                <SavedSettingToast
                    state={
                        props.savingToastKey === SAVE_STATE_KEYS.CHAT_INTAKE_FIELDS
                            ? props.toastState
                            : 'initial'
                    }
                >
                    <Body isBold={true} size='small'>
                        Fields to collect
                    </Body>
                </SavedSettingToast>
                <Checkbox label='Name (Minimum required)' checked={true} disabled={true} />
                <Checkbox
                    label='Email address'
                    checked={props.intakeFormFields.includes(EMAIL_FIELD)}
                    onChange={() => {
                        const newFields = toggleField(EMAIL_FIELD, props.intakeFormFields);
                        props.setIntakeFormFields(newFields);
                        props.handleSave(
                            {
                                fields: newFields,
                            },
                            SAVE_STATE_KEYS.CHAT_INTAKE_FIELDS
                        );
                    }}
                />
                <Checkbox
                    label='Phone number'
                    checked={props.intakeFormFields.includes(PHONE_FIELD)}
                    onChange={() => {
                        const newFields = toggleField(PHONE_FIELD, props.intakeFormFields);
                        props.setIntakeFormFields(newFields);
                        props.handleSave(
                            {
                                fields: newFields,
                            },
                            SAVE_STATE_KEYS.CHAT_INTAKE_FIELDS
                        );
                    }}
                />
                <Checkbox
                    label='Company name'
                    checked={props.intakeFormFields.includes(COMPANY_FIELD)}
                    onChange={() => {
                        const newFields = toggleField(COMPANY_FIELD, props.intakeFormFields);
                        props.setIntakeFormFields(newFields);
                        props.handleSave(
                            {
                                fields: newFields,
                            },
                            SAVE_STATE_KEYS.CHAT_INTAKE_FIELDS
                        );
                    }}
                />
            </Stack>
        </Stack>
    );
};
